import styled, { css, keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import Colors from 'styles/colors';
import { sansSerif, dropShadow } from 'styles/mixins';
import { AI_FAB_ICON } from './constants';

// Need a wrapper for positioning FAB. Fixed (not absolute) so the FAB stays
// pinned to the viewport corner on pages taller than the viewport. The
// z-index must live here: position fixed creates a stacking context, so the
// FAB's own z-index no longer competes with page content.
export const AISearchWrapper = styled.div`
    position: fixed;
    width: 100vw;
    height: 100vh;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    pointer-events: none;
    z-index: 9999999;
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const typingBounce = keyframes`
  0%, 80%, 100% {
    transform: scale(0.6);
    opacity: 0.4;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
`;

// Chat Container
export const ChatContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: ${Colors.whiteSmoke};
  ${sansSerif}
`;

// Messages List
export const MessagesList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;

  &[hidden] {
    display: none;
  }
`;

// Message Row (contains avatar + bubble)
export const MessageRow = styled.div`
  display: flex;
  gap: 10px;
  max-width: 100%;
  animation: ${slideIn} 0.2s ease-out;
  
  ${props => props.$role === 'user' && css`
    flex-direction: row-reverse;
  `}
`;

// Avatar
export const MessageAvatar = styled.img`
  width: 28px;
  height: 28px;
  flex-shrink: 0;
`;

// Message Bubble
export const MessageBubble = styled.div`
  max-width: calc(100% - 40px);
  padding: 12px 16px;
  border-radius: 12px;
  background: ${props => props.$role === 'user' ? Colors.pictonBlue : Colors.pureWhite};
  color: ${props => props.$role === 'user' ? Colors.pureWhite : Colors.fiord};
  ${dropShadow}
  
  ${props => props.$role === 'user' && css`
    border-bottom-right-radius: 4px;
  `}
  
  ${props => props.$role === 'assistant' && css`
    border-bottom-left-radius: 4px;
  `}
`;

/* Reasoning trail: shows what the assistant considered and which tools it
   ran. Collapsed by default so it informs without dominating the reply. */
export const ReasoningBlock = styled.details`
  margin-bottom: 8px;
  font-size: 12px;
  color: ${Colors.nevada};

  summary {
    cursor: pointer;
    color: ${Colors.curiousBlue};
    outline: none;
    user-select: none;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  &[open] summary {
    margin-bottom: 6px;
  }
`;

export const ReasoningStep = styled.div`
  padding: 6px 8px;
  margin-top: 4px;
  border-left: 2px solid ${Colors.onahau};
  background: ${Colors.whiteSmoke};
  border-radius: 0 3px 3px 0;
  line-height: 1.45;
  font-style: italic;
`;

export const ToolChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 6px;
`;

export const ToolChip = styled.span`
  display: inline-block;
  padding: 1px 7px;
  border-radius: 10px;
  font-size: 10px;
  font-style: normal;
  background: ${Colors.fadedGlaciers};
  color: ${Colors.curiousBlue};
`;

// Message Content
export const MessageContent = styled.div`
  font-size: 14px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-wrap: break-word;
`;

// Message Timestamp
export const MessageTimestamp = styled.div`
  font-size: 11px;
  color: ${props => props.$role === 'user' ? 'rgba(255,255,255,0.7)' : Colors.regentGray};
  margin-top: 6px;
`;

// Links Container
export const LinksContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 10px;
`;

export const MessageLink = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: ${props => props.$inUserMessage ? Colors.pureWhite : Colors.curiousBlue};
  text-decoration: none;
  font-size: 13px;
  padding: 6px 10px;
  border-radius: 4px;
  background: ${props => props.$inUserMessage ? 'rgba(255, 255, 255, 0.15)' : Colors.fadedGlaciers};
  transition: all 0.15s ease;
  cursor: pointer;
  
  &:hover {
    background: ${props => props.$inUserMessage ? 'rgba(255, 255, 255, 0.25)' : Colors.onahau};
  }
  
  &::before {
    content: '→';
  }
`;

// Actions Container
export const ActionsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
`;

export const ActionButton = styled.button`
  padding: 8px 14px;
  border: none;
  border-radius: 4px;
  background: ${Colors.pictonBlue};
  color: ${Colors.pureWhite};
  font-size: 13px;
  ${sansSerif}
  cursor: pointer;
  transition: all 0.15s ease;
  
  &:hover {
    filter: brightness(115%);
  }
  
  &:disabled {
    background: ${Colors.greyChateau};
    cursor: not-allowed;
  }
`;

// Typing Indicator
export const TypingIndicator = styled.div`
  display: flex;
  gap: 10px;
  animation: ${slideIn} 0.2s ease-out;
`;

export const TypingBubble = styled.div`
  background: ${Colors.pureWhite};
  padding: 14px 18px;
  border-radius: 12px;
  border-bottom-left-radius: 4px;
  ${dropShadow}
  display: flex;
  gap: 5px;
  align-items: center;
  
  span {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${Colors.pictonBlue};
    animation: ${typingBounce} 1.4s infinite ease-in-out;
    
    &:nth-child(1) {
      animation-delay: -0.32s;
    }
    
    &:nth-child(2) {
      animation-delay: -0.16s;
    }
  }
`;

// Input Container
export const InputContainer = styled.div`
  padding: 16px 20px;
  background: ${Colors.pureWhite};
  border-top: 1px solid ${Colors.mercury};
`;

export const InputWrapper = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-end;
  background: ${Colors.whiteSmoke};
  border: 1px solid ${Colors.mercury};
  border-radius: 8px;
  padding: 8px 12px;
  transition: all 0.15s ease;
  
  &:focus-within {
    border-color: ${Colors.pictonBlue};
  }
`;

export const ChatInput = styled.textarea`
  flex: 1;
  border: none;
  background: transparent;
  resize: none;
  min-height: 24px;
  max-height: 100px;
  font-size: 14px;
  line-height: 1.5;
  ${sansSerif}
  outline: none;
  color: ${Colors.fiord};
  padding: 0;
  
  &::placeholder {
    color: ${Colors.cadetBlue};
  }
  
  &:disabled {
    opacity: 0.6;
  }
`;

export const SendButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${Colors.pictonBlue};
  transition: all 0.15s ease;
  flex-shrink: 0;
  
  svg {
    width: 20px;
    height: 20px;
  }
  
  &:hover:not(:disabled) {
    color: ${Colors.curiousBlue};
  }
  
  &:disabled {
    color: ${Colors.cadetBlue};
    cursor: not-allowed;
  }
`;

export const InputHint = styled.div`
  font-size: 11px;
  color: ${Colors.regentGray};
  margin-top: 8px;
  text-align: center;
`;

// Empty State / Welcome
export const WelcomeState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 40px 20px;
  text-align: center;
`;

export const WelcomeIcon = styled.img`
  width: 48px;
  height: 48px;
  margin-bottom: 20px;
`;

export const WelcomeTitle = styled.h4`
  margin: 0 0 8px 0;
  font-size: 18px;
  font-weight: 600;
  color: ${Colors.fiord};
`;

export const WelcomeText = styled.p`
  margin: 0 0 24px 0;
  font-size: 14px;
  line-height: 1.5;
  max-width: 280px;
  color: ${Colors.nevada};
`;

export const Suggestions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
`;

export const SuggestionButton = styled.button`
  background: ${Colors.pureWhite};
  border: 1px solid ${Colors.mercury};
  border-radius: 20px;
  padding: 8px 16px;
  font-size: 13px;
  color: ${Colors.nevada};
  ${sansSerif}
  cursor: pointer;
  transition: all 0.15s ease;
  
  &:hover {
    background: ${Colors.whiteSmoke};
    border-color: ${Colors.pictonBlue};
    color: ${Colors.pictonBlue};
  }
`;

// Context Indicator
export const ContextIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: ${Colors.fadedGlaciers};
  border-bottom: 1px solid ${Colors.onahau};
  font-size: 12px;
  color: ${Colors.curiousBlue};
  ${sansSerif}
`;

export const ContextIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    width: 14px;
    height: 14px;
  }
`;

export const ContextText = styled.span`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const ContextLabel = styled.span`
  font-weight: 600;
  margin-right: 4px;
`;

// Lifecycle flag bar — shown when the open article carries a lifecycle
// classification (Stale / Contradicted / ...), offering the Unstale action.
export const LifecycleBar = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 8px 16px;
  background: ${Colors.drover};
  border-bottom: 1px solid ${Colors.sahara};
  font-size: 12px;
  color: ${Colors.sahara};
  ${sansSerif}
`;

export const LifecycleFlags = styled.span`
  flex: 1;
  min-width: 120px;

  strong {
    font-weight: 600;
  }
`;

export const UnstaleButton = styled.button`
  background: ${Colors.pureWhite};
  border: 1px solid ${Colors.sahara};
  border-radius: 20px;
  padding: 4px 14px;
  font-size: 12px;
  font-weight: 600;
  color: ${Colors.sahara};
  ${sansSerif}
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: ${Colors.sahara};
    color: ${Colors.pureWhite};
  }

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

// Chat menu (top bar): History, current chat title, New chat
export const MenuBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 12px;
  background: ${Colors.pureWhite};
  border-bottom: 1px solid ${Colors.mercury};
  flex-shrink: 0;
`;

export const MenuButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  min-height: 32px;
  padding: 4px 10px;
  background: transparent;
  border: 1px solid ${Colors.mercury};
  border-radius: 4px;
  ${sansSerif}
  font-size: 13px;
  color: ${Colors.nevada};
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition: all 0.15s ease;

  &:hover {
    background: ${Colors.whiteSmoke};
    border-color: ${Colors.pictonBlue};
    color: ${Colors.pictonBlue};
  }

  &:focus-visible {
    outline: 2px solid ${Colors.pictonBlue};
    outline-offset: 2px;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

export const MenuCount = styled.span`
  min-width: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: ${Colors.mercury};
  font-size: 11px;
  line-height: 18px;
  text-align: center;
  color: ${Colors.fiord};
`;

export const MenuTitle = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: center;
  font-size: 13px;
  font-weight: 600;
  color: ${Colors.fiord};
`;

// Chat history pane: replaces the message list while open
export const HistoryPane = styled.div`
  outline: none;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

export const HistorySearch = styled.input`
  margin: 12px 16px 4px;
  padding: 8px 12px;
  border: 1px solid ${Colors.mercury};
  border-radius: 8px;
  background: ${Colors.pureWhite};
  ${sansSerif}
  font-size: 14px;
  color: ${Colors.fiord};

  &::placeholder {
    color: ${Colors.cadetBlue};
  }

  &:focus {
    outline: none;
    border-color: ${Colors.pictonBlue};
  }
`;

export const HistoryScroll = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 16px 16px;
`;

export const HistoryGroupLabel = styled.h5`
  margin: 16px 0 6px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${Colors.regentGray};
`;

export const HistoryRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 4px;
  margin-bottom: 6px;
  background: ${Colors.pureWhite};
  border: 1px solid ${props => props.$active ? Colors.pictonBlue : Colors.mercury};
  border-radius: 8px;

  &:hover {
    border-color: ${Colors.pictonBlue};
  }
`;

export const HistoryOpen = styled.button`
  flex: 1;
  min-width: 0;
  padding: 10px 4px 10px 12px;
  background: transparent;
  border: none;
  border-radius: 8px;
  ${sansSerif}
  text-align: left;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${Colors.pictonBlue};
    outline-offset: -2px;
  }
`;

export const HistoryPrompt = styled.span`
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  overflow-wrap: anywhere;
  font-size: 14px;
  line-height: 1.4;
  color: ${Colors.fiord};
`;

export const HistoryMeta = styled.span`
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: ${Colors.regentGray};
`;

export const HistoryDelete = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  margin: 4px 4px 0 0;
  background: transparent;
  border: none;
  border-radius: 50%;
  color: ${Colors.regentGray};
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    background: ${Colors.mercury};
    color: ${Colors.fiord};
  }

  &:focus-visible {
    outline: 2px solid ${Colors.pictonBlue};
  }

  svg {
    width: 12px;
    height: 12px;
  }
`;

export const HistoryEmpty = styled.p`
  margin: 32px 0;
  text-align: center;
  font-size: 14px;
  line-height: 1.5;
  color: ${Colors.nevada};
`;

// Created content card — a draft or brief the assistant just made, with the
// articles it drew on and a button to open it.
export const CreatedCard = styled.div`
  margin-top: 12px;
  padding: 12px;
  border: 1px solid ${Colors.hippieGreen};
  border-left-width: 4px;
  border-radius: 8px;
  background: ${Colors.pureWhite};
  ${sansSerif}
`;

export const CreatedHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  color: ${Colors.regentGray};
  margin-bottom: 6px;
`;

export const CreatedBadge = styled.span`
  padding: 1px 6px;
  border-radius: 3px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  background: ${Colors.blueRomance};
  color: ${Colors.hippieGreen};
`;

export const CreatedTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${Colors.nevada};
  line-height: 1.35;
  margin-bottom: 8px;
`;

export const CreatedSources = styled.div`
  font-size: 12px;
  margin-bottom: 10px;

  ul {
    margin: 4px 0 0;
    padding-left: 18px;
  }

  li {
    margin: 2px 0;
  }

  a {
    color: ${Colors.curiousBlue};
  }
`;

export const CreatedSourcesLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: ${Colors.regentGray};
`;

export const CreatedOpenLink = styled(Link)`
  display: inline-block;
  padding: 5px 14px;
  border-radius: 20px;
  background: ${Colors.hippieGreen};
  color: ${Colors.pureWhite};
  font-size: 12px;
  font-weight: 600;
  text-decoration: none;

  &:hover,
  &:focus {
    color: ${Colors.pureWhite};
    opacity: 0.9;
  }
`;

// Brief bar — shown on the article list while rows are checked, offering to
// brief from them.
export const SelectionBar = styled(LifecycleBar)`
  background: ${Colors.fadedGlaciers};
  border-bottom-color: ${Colors.curiousBlue};
  color: ${Colors.curiousBlue};
`;

export const BriefButton = styled(UnstaleButton)`
  border-color: ${Colors.curiousBlue};
  color: ${Colors.curiousBlue};

  &:hover:not(:disabled) {
    background: ${Colors.curiousBlue};
  }
`;

// Search Result Card styles (inline in chat messages)
export const SearchResultsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 12px;
`;

export const SearchResultCard = styled.div`
  background: ${Colors.whiteSmoke};
  border: 1px solid ${Colors.mercury};
  border-radius: 8px;
  padding: 12px;
  transition: all 0.15s ease;

  &:hover {
    border-color: ${Colors.pictonBlue};
    background: ${Colors.pureWhite};
  }
`;

export const SearchResultHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 6px;
`;

/* Featured image thumbnail in a chat result card */
export const ResultThumbnail = styled.img`
  width: 56px;
  height: 42px;
  flex-shrink: 0;
  object-fit: cover;
  border-radius: 3px;
  background: ${Colors.mercury};
`;

export const SearchResultTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${Colors.nileBlue};
  line-height: 1.3;
  flex: 1;
`;

export const SearchResultScore = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

export const ScoreBar = styled.div`
  width: 40px;
  height: 4px;
  background: ${Colors.mercury};
  border-radius: 2px;
  overflow: hidden;
`;

export const ScoreFill = styled.div`
  height: 100%;
  border-radius: 2px;
  background: ${props => {
    if (props.$score >= 0.8) return Colors.chateauGreen;
    if (props.$score >= 0.6) return Colors.pictonBlue;
    return Colors.cadetBlue;
  }};
  width: ${props => Math.round(props.$score * 100)}%;
`;

export const ScoreLabel = styled.span`
  font-size: 10px;
  color: ${Colors.regentGray};
  min-width: 28px;
  text-align: right;
`;

export const SearchResultMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: ${Colors.regentGray};
  margin-bottom: 6px;
`;

export const ContentTypeBadge = styled.span`
  display: inline-block;
  padding: 1px 6px;
  border-radius: 3px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  background: ${props => {
    switch (props.$type) {
      case 'Article': return Colors.fadedGlaciers;
      case 'Event': return Colors.blueRomance;
      case 'Product': return Colors.drover;
      case 'Publication': return Colors.roseWhite;
      default: return Colors.whiteSmoke;
    }
  }};
  color: ${props => {
    switch (props.$type) {
      case 'Article': return Colors.curiousBlue;
      case 'Event': return Colors.hippieGreen;
      case 'Product': return Colors.sahara;
      case 'Publication': return Colors.roseTaupe;
      default: return Colors.nevada;
    }
  }};
`;

export const SourceBadge = styled.span`
  display: inline-block;
  padding: 1px 6px;
  border-radius: 3px;
  font-size: 10px;
  background: ${props => {
    switch (props.$source) {
      case 'semantic': return '#F3E8FF';
      case 'keyword': return Colors.fadedGlaciers;
      case 'both': return Colors.blueRomance;
      default: return Colors.whiteSmoke;
    }
  }};
  color: ${props => {
    switch (props.$source) {
      case 'semantic': return Colors.purple;
      case 'keyword': return Colors.curiousBlue;
      case 'both': return Colors.hippieGreen;
      default: return Colors.nevada;
    }
  }};
`;

export const MatchReason = styled.div`
  font-size: 11px;
  color: ${Colors.nevada};
  font-style: italic;
  margin-bottom: 6px;
  padding-left: 12px;
  border-left: 2px solid ${Colors.mercury};
`;

export const TagsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 8px;
`;

export const TagChip = styled.span`
  display: inline-block;
  padding: 1px 6px;
  border-radius: 10px;
  font-size: 10px;
  background: ${Colors.mercury};
  color: ${Colors.fiord};
`;

export const SearchResultActions = styled.div`
  display: flex;
  gap: 6px;
`;

export const SmallActionButton = styled.button`
  padding: 4px 10px;
  border: 1px solid ${Colors.mercury};
  border-radius: 4px;
  background: ${Colors.pureWhite};
  color: ${Colors.nevada};
  font-size: 11px;
  ${sansSerif}
  cursor: pointer;
  transition: all 0.15s ease;
  display: flex;
  align-items: center;
  gap: 4px;

  svg {
    width: 12px;
    height: 12px;
  }

  &:hover {
    border-color: ${Colors.pictonBlue};
    color: ${Colors.pictonBlue};
    background: ${Colors.fadedGlaciers};
  }
`;

export const AISearchFAB = styled.button`
  padding: 0;
  border-radius: 50%;
  z-index: 9999999;
  position: absolute;
  right: 24px;
  bottom: 24px;
  width: 56px;
  height: 56px;
  pointer-events: auto;


  /* The Ask My Brand mark is full-colour, so it sits on a white disc rather
     than the old blue gradient. Width-only background-size keeps its 575x500
     ratio instead of squashing it into a square. */
  background-color: #fff;
  background-image: url("${AI_FAB_ICON}");
  background-size: 30px auto;
  background-repeat: no-repeat;
  background-position: center;

  /* Subtle shadow with depth */
  box-shadow:
    0 4px 20px rgba(37, 110, 163, 0.3),
    0 6px 30px rgba(37, 110, 163, 0.2),
    inset 0 1px 1px rgba(255, 255, 255, 0.2);

  /* Remove default button styling */
  border: none;
  outline: none;
  cursor: pointer;

  /* Smooth transitions */
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  
  /* Hidden state */
  ${props => props.$hidden && css`
    opacity: 0;
    transform: translateY(100px) scale(0.5);
    pointer-events: none;
  `}
  
  /* Interaction states */
  &:hover {
    transform: translateY(-2px) scale(1.05);
    box-shadow:
      0 8px 25px rgba(37, 110, 163, 0.4),
      0 12px 40px rgba(37, 110, 163, 0.3),
      inset 0 1px 1px rgba(255, 255, 255, 0.3);
  }

  &:active {
    transform: translateY(0) scale(0.98);
    box-shadow:
      0 2px 10px rgba(37, 110, 163, 0.3),
      0 4px 15px rgba(37, 110, 163, 0.2),
      inset 0 1px 1px rgba(0, 0, 0, 0.1);
    transition-duration: 0.1s;
  }

  &:focus {
    outline: none;
    box-shadow:
      0 0 0 3px rgba(37, 110, 163, 0.3),
      0 4px 20px rgba(37, 110, 163, 0.3);
  }
  
  /* Optional: Add a subtle pulse animation */
  @keyframes subtle-pulse {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.02); }
  }
  
  /* Apply pulse animation only on initial load or specific state */
  &.pulse {
    animation: subtle-pulse 2s ease-in-out infinite;
  }
  
  /* Responsive adjustments */
  @media (max-width: 768px) {
    right: 16px;
    bottom: 16px;
    width: 52px;
    height: 52px;
    background-size: 27px auto;
  }

  @media (max-width: 480px) {
    right: 12px;
    bottom: 12px;
    width: 48px;
    height: 48px;
    background-size: 25px auto;
  }
`;